// Build data
const { env, envProduction, frontendUrl, institutionUrl, defaultDocumentTitle, institutionName, projectReference } = require('../rv_data.js') || {}
// Vue
import Vue from 'vue'
import Vuex from 'vuex'
Vue.use(Vuex)

const store = new Vuex.Store({
  strict: envProduction,
  state: {
    // Build
    environment: env,
    frontendUrl,
    institutionUrl,
    institutionName,
    projectReference,
    defaultDocumentTitle,
    // Populate
    error: null,
    posts: [],
    options: {},
    settings: {},
    labels: {},
    styles: {},
    // Browser
    browser: '',
    isTouch: false,
    windowDimensions: {},
    lteLaptop: false,
    lteTablet: false,
    lteMobile: false,
    // Nav
    loading: true,
    route: {},
    slug: '',
    justExploreBack: {},
    pageMeta: {
      pageTitle: '',
      parentTitle: ''
    },
    // Modals
    menuModalOpen: false,
    liveEventsModalOpen: false,
    contentModal: '',
  },

  getters: {
    // Lists
    posts: state => state.posts,
    pages: state => state.posts.filter(({ type }) => type === 'page'),
    AreasOfInterest: state => state.posts.filter(({ type }) => type === 'area-of-interest'),
    topLevelAoi: (state, getters) => getters.AreasOfInterest
      // Filter for top level AreasOfInterest
      .filter(({ parent }) => !parent )
      // Filter out the explore post - used to create the 'just explore'
      .filter(({ explore }) => !explore),
    medias: state => state.posts.filter(({ type }) => ['group', 'panorama', 'gallery', 'video', 'article' ].includes(type)),
    tourGroups: state => state.posts.filter(({ type }) => 'group' === type),
    // Get
    getPostBySlug: state => slug => state.posts.find(item => item.slug === slug),
    getAoiBySlug: (state, getters) => slug => getters.AreasOfInterest.find(item => item.slug === slug),
    getMediaBySlug: (state, getters) => slug => getters.medias.find(item => item.slug === slug),
    getTourGroupBySlug: (state, getters) => slug => getters.tourGroups.find(item => item.slug === slug),
    // Route
    routeName: state => state.route.name || 'home',
    routeParams: state => state.route.params || {},
    isContentspot: (state, getters) => ['aoi-contentspot', 'gt-contentspot', 'je-contentspot'].indexOf(getters.routeName) !== -1,
    // Get Specific
    post: state => state.posts.find(({ slug }) => slug === state.slug),
    homePage: (state, getters) => getters.pages.find(({ template }) => template === 'page-home.php'),
    mainMenuPage: (state, getters) => getters.pages.find(({ template }) => template === 'page-main-menu.php'),
    // Area of interest
    aoiPage: (state, getters) => getters.pages.find(({ template }) => template === 'page-aoi.php'),
    routeAoi: (state, getters) => getters.getAoiBySlug(getters.routeParams.aoi_id),
    // Guided tour
    guidedTourPage: (state, getters) => getters.pages.find(({ template }) => template === 'page-guided-tour.php'),
    isGuidedTour: (state, getters) => ['guided-tour', 'gt-panorama', 'gt-contentspot'].includes(getters.routeName),
    routeGuidedTour: (state, getters) => getters.isGuidedTour && getters.guidedTourPage,
    // Just explore
    justExplorePage: (state, getters) => getters.pages.find(({ template }) => template === 'page-explore.php'),
    isJustExplore: (state, getters) => ['je-panorama', 'je-contentspot'].includes(getters.routeName),
    routeJustExplore: (state, getters) => getters.isJustExplore && getters.justExplorePage,
    // Media
    routeMedia: (state, getters) => getters.getMediaBySlug(getters.routeParams.media_id),
    routePanorama: (state, getters) => getters.getMediaBySlug(getters.routeParams.panorama_id),
    routeContentspot: (state, getters) => getters.getMediaBySlug(getters.routeParams.contentspot_id),
    // Other
    logos: (state) => {
      const { options } = state || {}
      const { logo_white, logo_color } = options || {}
      return {
        white: logo_white,
        color: logo_color,
      }
    },
    // Get nested data from store. Mainly used for styles and labels
    myStore: (state) => (objectRoute) => {
      let target = state
      if (!target) { return undefined }
      objectRoute.forEach((key) => {
        if (!target) { return undefined }
        target = target[key]
      })
      return target
    },
    // Unibuddy
    thirdPartyOverrideRouteMatch: (state, getters) => {
      const { routeName, routeAoi, routeGuidedTour, routeJustExplore } = getters
      // Validation
      const routeNamesToHide = ['home']
      const isProhibitedRoute = routeNamesToHide.includes(routeName)
      if (isProhibitedRoute) { return {} }
      // Specific
      return routeAoi || routeGuidedTour || routeJustExplore
    },
    unibuddyFilterObj: (state, getters) => {
      const { thirdPartyOverrideRouteMatch } = getters
      // Get Values
      const { unibuddy } = thirdPartyOverrideRouteMatch || {}
      const { enabled, filter_key, filter_value } = unibuddy || {}
      const specificFilterKey = enabled && filter_key
      const specificFilterValue = enabled && filter_value
      // Generic
      const genericFilterKey = getters.myStore(['options', 'unibuddy', 'filter_key']) ?? ''
      const genericFilterValue = getters.myStore(['options', 'unibuddy', 'filter_value']) ?? ''
      // Return
      return {
        filterKey: specificFilterKey || genericFilterKey,
        filterValue: specificFilterValue || genericFilterValue
      }
    },
    tapFilterObj: (state, getters) => {
      const { thirdPartyOverrideRouteMatch } = getters
      // Get Values
      const { tap } = thirdPartyOverrideRouteMatch || {}
      const { enabled, terms, href } = tap || {}
      const validTerms = terms || []
      const specificTerms = enabled && validTerms.length && validTerms
      const specificHref = enabled && href
      // Generic
      const genericTerms = getters.myStore(['options', 'tap', 'terms']) ?? []
      const genericHref = getters.myStore(['options', 'tap', 'href']) ?? ''
      // Return
      return {
        terms: specificTerms || genericTerms,
        href: specificHref || genericHref
      }
    },
  },

  mutations: {
    populate(state, { basis, options, settings, error } = {}) {
      // Error
      state.error = error

      // Subject Area post type override (hide as area-of-interest without clogginging up the admin-list)
      const saEnabled = ((settings || {}).acf || {}).subject_areas_enabled

      // Posts
      const posts = basis
        .map((item, index, self) => {
          // Validation
          const isGroup = item.type === 'group'
          const isJustExplore = item.template === 'page-explore'
          const needsChildren = isGroup || isJustExplore
          const children = item.acf.children || []
          if (needsChildren && !children.length) { return }
          // REturn
          return {
            id: item.id,
            type: item.type === 'post' ? 'article' : item.type,
            slug: item.slug,
            title: item.title.rendered,
            parent: item.parent,
            template: item.template,
            ...item.acf,
          }
        })
        // Filter Failures
        .filter(Boolean)
        // Children
        .map((item, index, self) => {
          const isSaParent = item.is_sa_parent
          const isSubjectArea = 'subject-area' == item.type
          // Featured Content)
          if (item.featured_content) {
            const fcObj = self.find(({ id }) => id === item.featured_content)
            if (fcObj) { item.featuredContent = fcObj }
          }
          delete item.featured_content
          // Main Video (DEPRECATED. SUPERCEDED BY FEATURED CONTENT)
          if (item.main_video) {
            const videoObj = self.find(({ id }) => id === item.main_video)
            if (videoObj) { item.mainVideo = videoObj }
          }
          delete item.main_video
          // Subject area
          const saParent = isSaParent ? item : self.find(({ is_sa_parent }) => is_sa_parent)
          if (saEnabled && saParent && isSubjectArea) {
            item.parent = item.parent || saParent.id
          } else if (isSaParent) {
            delete item.subheading
            delete item.content
          }
          // Return
          return item
        })
        // Children
        .map((item, index, self) => {
          // Content children
          const contentChildren = (item.children || [])
            .map((child) => {
              // Group
              if (!child.entry) { return self.find(({ id }) => id === child) }
              // Contentspots / GT
              const entryObj = self.find(({ id }) => id === child.entry)

              if (!entryObj) { return }
              delete child.entry
              return {
                ...entryObj,
                ...child
              }
            })
            // Filter Failures
            .filter(Boolean)
          const aoiChildren = self.filter(({ parent }) => parent === item.id)
          const isSubjectArea = 'subject-area' == item.type
          if (isSubjectArea) { aoiChildren.sort((a, b) => a.slug > b.slug ? 1 : -1) }
          item.children = [
            ...aoiChildren,
            ...contentChildren
          ]
          // Return
          return item
        })
        // Parent
        .map((item, index, self) => {
          item.parent = item.parent && self.find(({ id }) => id === item.parent)
          // Return
          return item
        })
        // Subject areas
        .map((item, index, self) => {
          if ('subject-area' == item.type) {
            item.isSubjectArea = true
            item.type = 'area-of-interest'
          }
          if (!item.is_sa_parent) { return item }
          // Sort Subject Area children
          item.children.sort((a, b) => a.slug > b.slug ? 1 : -1)
          if (!item.children.length) { item.is_sa_parent = false }
          // Return
          return item
        })

      // Home page
      const homePage = posts.find(({ template }) => 'page-home.php' === template)
      if (homePage) { homePage.routeTo = { name: 'home' } }

      // Main menu page
      const mainMenuPage = posts.find(({ template }) => 'page-main-menu.php' === template)
      if (mainMenuPage) { mainMenuPage.routeTo = { name: 'main-menu' } }

      // Area of interest page override
      const aoiPage = posts.find(({ template }) => 'page-aoi.php' === template)
      if (aoiPage) { aoiPage.routeTo = { name: 'area-of-interest' } }

      // Area of interest page override
      const gtPage = posts.find(({ template }) => 'page-guided-tour.php' === template)
      if (gtPage) {
        const children = gtPage.children
        gtPage.routeTo = { name: 'guided-tour' }
        gtPage.children = children.map((item, i1) => {
          item.stopNo = i1 + 1
          item.gtRouteTo = {
            name: 'gt-panorama',
            params: { panorama_id: item.slug }
          }
          return item
        })
      }

      // Just explore page override
      const jePage = posts.find(({ template }) => 'page-explore.php' === template)
      if (jePage) {
        const children = jePage.children
        const firstChild = children.length && children[0]
        const firstChildSlug = firstChild && firstChild.slug
        const routeTo = {
          name: 'je-panorama',
          params: { panorama_id: firstChildSlug }
        }
        if (firstChildSlug) { jePage.routeTo = routeTo }
      }

      // Add posts
      state.posts = posts

      // ================================================================
      // Acf Site Options (client-facing)
      // ================================================================
      state.options = options.acf

      // ================================================================
      // Acf Admin settings (RV-only)
      // ================================================================
      const adminSettings = settings.acf || {}
      const adminLabels = adminSettings.labels
      delete adminSettings.labels
      const adminStyles = adminSettings.styles
      delete adminSettings.styles
      state.settings = adminSettings
      // ==========================================
      // Labels
      // ==========================================
      adminLabels.test = {}
      state.labels = adminLabels
      // ==========================================
      // Styles
      // ==========================================
      adminStyles.test = {}
      state.styles = adminStyles
    },
    // Browser stuff
    browser(state, value) { state.browser = value },
    isTouch(state) { state.isTouch = true },
    windowDimensions(state, value) { state.windowDimensions = value },
    lteLaptop(state, value) { state.lteLaptop = value },
    lteTablet(state, value) { state.lteTablet = value },
    lteMobile(state, value) { state.lteMobile = value },
    // Nav stuff
    isLoading(state, loading) { state.loading = loading },
    route(state, route) {
      state.route = route
      state.slug = route.path.split('/').pop() || '/'
    },
    justExploreBack(state, value) { state.justExploreBack = value },
    pageMeta(state, value) {
      if (!value) { return }
      const pageTitle = value.pageTitle
      state.pageMeta.pageTitle = pageTitle || ''
      const parentTitle = value.parentTitle
      state.pageMeta.parentTitle = parentTitle || ''
    },
    // Modals
    menuModalOpen(state, value) { state.menuModalOpen = value },
    liveEventsModalOpen(state, value) { state.liveEventsModalOpen = value },
    contentModal(state, value) { state.contentModal = value },
  },

  actions: {}
})

export default store
